import { useState, useEffect, useCallback } from 'react'
import type { MenuItem } from '@/types/menu'
import type { SelectedModifier, ComboGroupSelection, IngredientVariationSelection } from '@/types/cart'
import { getIngredientBasePrice, rowHasDynamicPricing } from '@/utils/ingredientPricing'

interface UseItemCustomizationReturn {
  quantity: number
  setQuantity: (qty: number) => void
  selectedModifiers: SelectedModifier[]
  setSelectedModifiers: React.Dispatch<React.SetStateAction<SelectedModifier[]>>
  mainMealCustomizations: SelectedModifier[]
  setMainMealCustomizations: React.Dispatch<React.SetStateAction<SelectedModifier[]>>
  comboGroupSelections: ComboGroupSelection[]
  setComboGroupSelections: React.Dispatch<React.SetStateAction<ComboGroupSelection[]>>
  ingredientVariationSelections: IngredientVariationSelection[]
  setIngredientVariationSelections: React.Dispatch<React.SetStateAction<IngredientVariationSelection[]>>
  specialInstructions: string
  setSpecialInstructions: (instructions: string) => void
  mainMealExpanded: boolean
  setMainMealExpanded: (expanded: boolean) => void
  resetState: () => void
}

export function useItemCustomization(item: MenuItem | null): UseItemCustomizationReturn {
  const [quantity, setQuantity] = useState(1)
  const [selectedModifiers, setSelectedModifiers] = useState<SelectedModifier[]>([])
  const [specialInstructions, setSpecialInstructions] = useState('')
  const [mainMealExpanded, setMainMealExpanded] = useState(false)
  const [mainMealCustomizations, setMainMealCustomizations] = useState<SelectedModifier[]>([])
  const [comboGroupSelections, setComboGroupSelections] = useState<ComboGroupSelection[]>([])
  const [ingredientVariationSelections, setIngredientVariationSelections] = useState<IngredientVariationSelection[]>([])

  const resetState = useCallback(() => {
    setQuantity(1)
    setSelectedModifiers([])
    setSpecialInstructions('')
    setMainMealExpanded(false)
    setMainMealCustomizations([])
    setComboGroupSelections([])
    setIngredientVariationSelections([])
  }, [])

  // Reset state and pre-select defaults when item changes
  useEffect(() => {
    if (item) {
      setQuantity(1)
      setSpecialInstructions('')
      setMainMealExpanded(false)

      // Pre-select default options for single item modifier groups
      if (item.itemType === 'single' && item.modifierGroups) {
        const defaults: SelectedModifier[] = []
        const singleVariationDefaults: IngredientVariationSelection[] = []

        // Default ingredient variation (size, etc.)
        if (item.ingredientVariationGroup) {
          const defaultVar = item.ingredientVariationGroup.variations.find((v) => v.is_default)
          if (defaultVar) {
            defaults.push({
              groupId: item.ingredientVariationGroup.id,
              groupName: item.ingredientVariationGroup.name,
              optionId: defaultVar.id,
              optionName: defaultVar.name,
              priceAdjustment: 0,
            })
          }
        }

        for (const group of item.modifierGroups) {
          const defaultOptions = group.options.filter((o) => o.isDefault)
          for (const opt of defaultOptions) {
            // For dynamic rows, store 0; for static rows, use resolved price
            const isDynamic = rowHasDynamicPricing(item, group.id)
            const price = isDynamic ? 0 : getIngredientBasePrice(item, group.id, opt.id)
            defaults.push({
              groupId: group.id,
              groupName: group.name,
              optionId: opt.id,
              optionName: opt.name,
              priceAdjustment: price,
              ...(group.selectionType === 'quantity' ? { quantity: 1 } : {}),
            })
            // Auto-select default variation for default modifier options
            if (opt.hasVariations && opt.variationGroup) {
              const defaultVar = opt.variationGroup.variations.find((v) => v.is_default)
              if (defaultVar) {
                singleVariationDefaults.push({
                  rowId: group.id,
                  rowIngredientId: opt.id,
                  ingredientName: opt.name,
                  variationGroupId: opt.variationGroup.id,
                  variationGroupName: opt.variationGroup.name,
                  variationId: defaultVar.id,
                  variationName: defaultVar.name,
                  priceAdjustment: defaultVar.price ?? 0,
                })
              }
            }
          }
        }
        setSelectedModifiers(defaults)
        if (singleVariationDefaults.length > 0) {
          setIngredientVariationSelections((prev) => [...prev, ...singleVariationDefaults])
        }
      } else {
        setSelectedModifiers([])
      }

      // Pre-select default options for combo groups
      if (item.comboProduct?.groups) {
        const defaults: ComboGroupSelection[] = []
        for (const group of item.comboProduct.groups) {
          const defaultOptions = group.options.filter((o) => o.isDefault)
          for (const opt of defaultOptions) {
            defaults.push({
              groupId: group.id,
              groupName: group.name,
              optionId: opt.id,
              optionName: opt.ingredient.name,
              additionalPrice: opt.additionalPrice,
            })
          }
        }
        setComboGroupSelections(defaults)
      } else {
        setComboGroupSelections([])
      }

      // Pre-select default ingredients for combo main meal rows
      if (item.comboProduct?.mainMeal?.rows) {
        const defaults: SelectedModifier[] = []
        const variationDefaults: IngredientVariationSelection[] = []
        for (const row of item.comboProduct.mainMeal.rows) {
          const defaultIngredients = row.ingredients.filter((i) => i.isDefault)
          for (const ing of defaultIngredients) {
            // For dynamic rows, store 0; for static rows, use resolved price
            const isDynamic = rowHasDynamicPricing(item, row.id)
            const price = isDynamic ? 0 : getIngredientBasePrice(item, row.id, ing.id)
            defaults.push({
              groupId: row.id,
              groupName: row.title,
              optionId: ing.id,
              optionName: ing.ingredient.name,
              priceAdjustment: price,
              ...(row.selectionType === 'quantity' ? { quantity: 1 } : {}),
            })
            // Auto-select default variation for default ingredients
            if (ing.ingredient.hasVariations && ing.ingredient.variationGroup) {
              const defaultVar = ing.ingredient.variationGroup.variations.find((v) => v.is_default)
              if (defaultVar) {
                variationDefaults.push({
                  rowId: row.id,
                  rowIngredientId: ing.id,
                  ingredientName: ing.ingredient.name,
                  variationGroupId: ing.ingredient.variationGroup.id,
                  variationGroupName: ing.ingredient.variationGroup.name,
                  variationId: defaultVar.id,
                  variationName: defaultVar.name,
                  priceAdjustment: defaultVar.price ?? 0,
                })
              }
            }
          }
        }
        setMainMealCustomizations(defaults)
        setIngredientVariationSelections(variationDefaults)
      } else {
        setMainMealCustomizations([])
        setIngredientVariationSelections([])
      }
    }
  }, [item])

  // Prune orphaned variation selections when ingredients are deselected
  useEffect(() => {
    setIngredientVariationSelections((prev) => {
      if (prev.length === 0) return prev
      // Keep variation selections that still have a matching selected modifier or main meal customization
      const allSelections = [...mainMealCustomizations, ...selectedModifiers]
      const pruned = prev.filter((vs) =>
        allSelections.some(
          (sel) => sel.groupId === vs.rowId && sel.optionId === vs.rowIngredientId
        )
      )
      if (pruned.length !== prev.length) return pruned
      return prev
    })
  }, [mainMealCustomizations, selectedModifiers])

  return {
    quantity,
    setQuantity,
    selectedModifiers,
    setSelectedModifiers,
    mainMealCustomizations,
    setMainMealCustomizations,
    comboGroupSelections,
    setComboGroupSelections,
    ingredientVariationSelections,
    setIngredientVariationSelections,
    specialInstructions,
    setSpecialInstructions,
    mainMealExpanded,
    setMainMealExpanded,
    resetState,
  }
}
